import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const fetchDomains = createAsyncThunk(
  'domains/fetchDomains',
  async ({ sortBy = 'createdAt', sortOrder = 'desc' } = {}) => {
    const response = await api.get(`/admin/domains?sortBy=${sortBy}&sortOrder=${sortOrder}`);
    return response.data;
  }
);

export const addDomain = createAsyncThunk(
  'domains/addDomain',
  async (domainData, { rejectWithValue }) => {
    try {
      const response = await api.post('/admin/domains', domainData);
      return response.data.data.domain;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to add domain';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateDomain = createAsyncThunk(
  'domains/updateDomain',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      console.log('Updating domain with data:', { id, data });
      const response = await api.put(`/admin/domains/${id}`, data);
      return response.data.data.domain;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to update domain';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteDomain = createAsyncThunk(
  'domains/deleteDomain',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/admin/domains/${id}`);
      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.error?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to delete domain';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchDomainAccess = createAsyncThunk(
  'domains/fetchDomainAccess',
  async (domainId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/admin/domains/${domainId}/access`);
      const accessList = response.data.data || [];
      const sortedAccessList = [...accessList].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      return { domainId, accessList: sortedAccessList };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch domain access');
    }
  }
);

const domainsSlice = createSlice({
  name: 'domains',
  initialState: {
    domains: [],
    loading: false,
    error: null,
    accessList: {},
    accessLoading: false,
    accessError: null,
    defaultTargetCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'IE'],
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAccessError: (state) => {
      state.accessError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Domains
      .addCase(fetchDomains.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDomains.fulfilled, (state, action) => {
        state.loading = false;
        state.domains = action.payload || [];
      })
      .addCase(fetchDomains.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add Domain
      .addCase(addDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDomain.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.domains.push(action.payload);
        }
      })
      .addCase(addDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Domain
      .addCase(updateDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDomain.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.domains.findIndex(domain => domain._id === action.payload._id);
          if (index !== -1) {
            state.domains[index] = action.payload;
          }
        }
      })
      .addCase(updateDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.domains = [...state.domains];
      })
      // Delete Domain
      .addCase(deleteDomain.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDomain.fulfilled, (state, action) => {
        state.loading = false;
        state.domains = state.domains.filter(domain => domain._id !== action.payload);
      })
      .addCase(deleteDomain.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Domain Access
      .addCase(fetchDomainAccess.pending, (state) => {
        state.accessLoading = true;
        state.accessError = null;
      })
      .addCase(fetchDomainAccess.fulfilled, (state, action) => {
        state.accessLoading = false;
        state.accessList = {
          ...state.accessList,
          [action.payload.domainId]: action.payload.accessList
        };
      })
      .addCase(fetchDomainAccess.rejected, (state, action) => {
        state.accessLoading = false;
        state.accessError = action.payload;
      });
  },
});

export const { clearError, clearAccessError } = domainsSlice.actions;
export default domainsSlice.reducer; 